import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import * as campaignApi from "../../../api/campaign";

/** Run states that mean the campaign is still sending. */
const ACTIVE_RUN_STATES = new Set(["running", "stopping"]);

export function useCampaigns() {
  return useQuery({
    queryKey: ["campaigns"],
    queryFn: () => campaignApi.listCampaigns(),
  });
}

export function useTriggerTypes() {
  return useQuery({
    queryKey: ["triggerTypes"],
    queryFn: () => campaignApi.getTriggerTypes(),
    // The trigger schema changes only with a deploy; refetching it on every
    // window focus is pure noise.
    staleTime: 10 * 60 * 1000,
  });
}

export function useCampaignLog(campaignId, offset = 0) {
  return useQuery({
    queryKey: ["campaignLog", campaignId, offset],
    queryFn: () => campaignApi.getCampaignLog(campaignId, offset),
    enabled: campaignId != null,
    placeholderData: (prev) => prev,
  });
}

export function useCampaignStats(campaignId) {
  return useQuery({
    queryKey: ["campaignStats", campaignId],
    queryFn: () => campaignApi.getCampaignStats(campaignId),
    enabled: campaignId != null,
    // The stats endpoint runs the trigger's real candidate query — a
    // correlated-subquery scan over the whole customer table for some
    // triggers. Refetching it on every focus re-ran that constantly.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSegmentDefinitions(campaignId) {
  return useQuery({
    queryKey: ["campaignSegments", campaignId ?? null],
    queryFn: () => campaignApi.getSegments(campaignId),
    staleTime: 30 * 1000,
  });
}

export function useCreateSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => campaignApi.createSegment(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaignSegments"] }),
  });
}

export function useDeleteSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => campaignApi.deleteSegment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaignSegments"] }),
  });
}

/**
 * Fetch the analytics SQL for a campaign configuration, on demand.
 *
 * Not a query: it is only needed when the operator presses "Copy SQL", and
 * the result depends on the config currently in the form, which changes with
 * every keystroke.
 */
export function useCampaignSql() {
  return useMutation({
    mutationFn: ({ triggerType, config }) =>
      campaignApi.getCampaignSql(triggerType, config),
  });
}

/**
 * Live run state for a campaign.
 *
 * Only polls while a run is actually in flight; once the run reaches a
 * terminal state the interval stops so an idle dashboard makes no requests.
 */
export function useCampaignRunState(campaignId) {
  return useQuery({
    queryKey: ["campaignRunState", campaignId],
    queryFn: () => campaignApi.getCampaignRunState(campaignId),
    enabled: campaignId != null,
    refetchInterval: (query) => {
      const state = query.state.data?.runState;
      return state && ACTIVE_RUN_STATES.has(state) ? 1500 : false;
    },
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => campaignApi.createCampaign(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => campaignApi.updateCampaign(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => campaignApi.deleteCampaign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

export function useRunCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => campaignApi.runCampaign(id),
    onSuccess: (data, id) => {
      qc.invalidateQueries({ queryKey: ["campaignRunState", id] });
      if (data?.alreadyRunning) {
        toast.error("Кампания уже выполняется");
        return;
      }
      // The campaign row now carries run state, so the list must refresh too.
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
}

export function useStopCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => campaignApi.stopCampaign(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["campaignRunState", id] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
}

export function useTestCampaign() {
  return useMutation({
    mutationFn: ({ id, phone }) => campaignApi.testCampaign(id, phone),
  });
}
